import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Users, Plus, Upload, Loader2, Trash2, X, ImageIcon, ChevronDown, ChevronUp, ZoomIn, ChevronLeft, ChevronRight, Pencil, Copy, Eye
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function AvatarStudio() {
  const { isAdmin } = useAuth();
  const [presets, setPresets] = useState([]);
  const [custom, setCustom] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [showAdminCreate, setShowAdminCreate] = useState(false);
  const [lightbox, setLightbox] = useState(null); // { avatar, index }
  const [editingAvatar, setEditingAvatar] = useState(null); // avatar object to edit
  const [showAllPresets, setShowAllPresets] = useState(false);
  const [regionFilter, setRegionFilter] = useState("all"); // "all" | "indian" | "american"

  const filteredPresets = regionFilter === "all" ? presets : presets.filter((p) => p.region === regionFilter);

  useEffect(() => {
    fetchAvatars();
  }, []);

  const fetchAvatars = async () => {
    try {
      const res = await axios.get(`${API}/api/avatars`, { withCredentials: true });
      setPresets(res.data.presets || []);
      setCustom(res.data.custom || []);
    } catch (err) {
      toast.error("Failed to load avatars");
    } finally {
      setLoading(false);
    }
  };

  const deleteAvatar = async (id) => {
    if (!window.confirm("Delete this avatar?")) return;
    try {
      await axios.delete(`${API}/api/avatars/${id}`, { withCredentials: true });
      toast.success("Avatar deleted");
      fetchAvatars();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete");
    }
  };

  const copyPresetToMine = async (id) => {
    try {
      await axios.post(`${API}/api/avatars/${id}/copy`, {}, { withCredentials: true });
      toast.success("Avatar added to your list!");
      fetchAvatars();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to add avatar");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="avatar-studio-loading">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto py-8 px-4 space-y-10" data-testid="avatar-studio-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Users className="w-6 h-6" />
            Avatar Studio
          </h1>
          <p className="text-muted-foreground text-sm mt-1">Browse preset avatars or create your own</p>
        </div>
        <Button
          onClick={() => setShowCreate(!showCreate)}
          className="rounded-full gap-2"
          disabled={custom.length >= 5}
          data-testid="create-avatar-btn"
        >
          <Plus className="w-4 h-4" />{custom.length >= 5 ? "Limit Reached (5/5)" : "Create Avatar"}
        </Button>
      </div>

      {/* Create Custom Avatar Form */}
      {showCreate && (
        <AvatarForm
          title="Create Your Avatar"
          subtitle="Upload 3 reference images to create a custom avatar"
          endpoint={`${API}/api/avatars`}
          onSuccess={() => { setShowCreate(false); fetchAvatars(); }}
          onCancel={() => setShowCreate(false)}
          testIdPrefix="custom"
        />
      )}

      {/* Admin: Create Preset Avatar */}
      {isAdmin && (
        <div>
          <button
            onClick={() => setShowAdminCreate(!showAdminCreate)}
            className="text-sm font-medium text-primary flex items-center gap-1 mb-3"
            data-testid="admin-toggle-preset-form"
          >
            {showAdminCreate ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            Admin: Add Preset Avatar
          </button>
          {showAdminCreate && (
            <AvatarForm
              title="Add Preset Avatar"
              subtitle="This avatar will be visible to all users in the gallery"
              endpoint={`${API}/api/avatars/preset`}
              onSuccess={() => { setShowAdminCreate(false); fetchAvatars(); }}
              onCancel={() => setShowAdminCreate(false)}
              testIdPrefix="preset"
            />
          )}
        </div>
      )}

      {/* Preset Avatars — Carousel + Expandable Grid */}
      <section>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold">Preset Avatars {presets.length > 0 && <span className="text-sm font-normal text-muted-foreground">({filteredPresets.length}{regionFilter !== "all" ? ` of ${presets.length}` : ""})</span>}</h2>
          {presets.length > 0 && (
            <button
              onClick={() => setShowAllPresets(!showAllPresets)}
              className="text-sm text-primary font-medium flex items-center gap-1 hover:underline"
              data-testid="toggle-all-presets"
            >
              {showAllPresets ? <><ChevronUp className="w-4 h-4" />Hide</> : <><Eye className="w-4 h-4" />View All</>}
            </button>
          )}
        </div>
        {presets.length > 0 && (
          <div className="flex items-center gap-2 mb-4" data-testid="preset-region-filters">
            {[
              { key: "all", label: "All" },
              { key: "indian", label: "Indian" },
              { key: "american", label: "American" },
            ].map((opt) => {
              const count = opt.key === "all" ? presets.length : presets.filter((p) => p.region === opt.key).length;
              const active = regionFilter === opt.key;
              return (
                <button
                  key={opt.key}
                  onClick={() => setRegionFilter(opt.key)}
                  disabled={count === 0}
                  className={`px-3 py-1 rounded-full text-xs font-medium transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${active ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground hover:bg-muted/70"}`}
                  data-testid={`preset-region-${opt.key}`}
                >
                  {opt.label} <span className="opacity-70">({count})</span>
                </button>
              );
            })}
          </div>
        )}
        {presets.length === 0 ? (
          <div className="text-center py-12 border-2 border-dashed border-border rounded-xl">
            <Users className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
            <p className="text-muted-foreground text-sm">No preset avatars yet.</p>
            {isAdmin && <p className="text-xs text-muted-foreground mt-1">Use the admin form above to add preset avatars.</p>}
          </div>
        ) : !showAllPresets ? (
          /* Carousel — hero shots only */
          <div className="relative">
            <div className="flex gap-4 overflow-x-auto pb-3 scrollbar-thin" data-testid="preset-carousel">
              {filteredPresets.map((avatar) => (
                <button
                  key={avatar.id}
                  onClick={() => setShowAllPresets(true)}
                  className="flex flex-col items-center gap-2 shrink-0 group"
                  data-testid={`carousel-item-${avatar.id}`}
                >
                  <div className="w-20 h-20 rounded-full overflow-hidden border-2 border-border group-hover:border-primary/50 transition-colors shadow-sm">
                    <img
                      src={(avatar.images || [])[0]}
                      alt={avatar.name}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  </div>
                  <span className="text-xs text-muted-foreground group-hover:text-foreground transition-colors font-medium max-w-[80px] truncate text-center">
                    {avatar.name.split(",")[0]}
                  </span>
                </button>
              ))}
              {/* View All pill at the end */}
              <button
                onClick={() => setShowAllPresets(true)}
                className="flex flex-col items-center gap-2 shrink-0"
                data-testid="carousel-view-all"
              >
                <div className="w-20 h-20 rounded-full border-2 border-dashed border-border hover:border-primary/50 transition-colors flex items-center justify-center bg-muted/30">
                  <Eye className="w-5 h-5 text-muted-foreground" />
                </div>
                <span className="text-xs text-primary font-medium">View All</span>
              </button>
            </div>
          </div>
        ) : (
          /* Full card grid */
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
            {filteredPresets.map((avatar) => (
              <AvatarCard key={avatar.id} avatar={avatar} onDelete={isAdmin ? deleteAvatar : null} onEdit={null} onCopy={custom.length < 5 ? copyPresetToMine : null} onPreview={(idx) => setLightbox({ avatar, index: idx })} />
            ))}
          </div>
        )}
      </section>

      {/* Custom Avatars */}
      <section>
        <h2 className="text-lg font-semibold mb-4">My Avatars <span className="text-sm font-normal text-muted-foreground">({custom.length}/5)</span></h2>
        {custom.length === 0 ? (
          <div className="text-center py-12 border-2 border-dashed border-border rounded-xl">
            <ImageIcon className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
            <p className="text-muted-foreground text-sm">You haven't created any avatars yet.</p>
            <Button variant="outline" size="sm" className="mt-3 rounded-full gap-1" onClick={() => setShowCreate(true)}>
              <Plus className="w-3 h-3" />Create One
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
            {custom.map((avatar) => (
              <AvatarCard key={avatar.id} avatar={avatar} onDelete={deleteAvatar} onEdit={setEditingAvatar} onPreview={(idx) => setLightbox({ avatar, index: idx })} />
            ))}
          </div>
        )}
      </section>

      {/* Edit Avatar Modal */}
      {editingAvatar && (
        <div className="fixed inset-0 z-[90] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setEditingAvatar(null)}>
          <div className="w-full max-w-lg" onClick={(e) => e.stopPropagation()}>
            <EditAvatarForm
              avatar={editingAvatar}
              onSuccess={() => { setEditingAvatar(null); fetchAvatars(); }}
              onCancel={() => setEditingAvatar(null)}
            />
          </div>
        </div>
      )}

      {/* Lightbox */}
      {lightbox && (
        <AvatarLightbox
          avatar={lightbox.avatar}
          initialIndex={lightbox.index}
          onClose={() => setLightbox(null)}
        />
      )}
    </div>
  );
}


function AvatarCard({ avatar, onDelete, onEdit, onCopy, onPreview }) {
  return (
    <div
      className="group relative rounded-xl border border-border bg-card overflow-hidden transition-shadow hover:shadow-md"
      data-testid={`avatar-card-${avatar.id}`}
    >
      {/* 3 images in a row */}
      <div className="grid grid-cols-3 gap-px bg-border">
        {(avatar.images || []).map((img, i) => (
          <button
            key={i}
            className="aspect-square bg-muted relative cursor-pointer group/img"
            onClick={() => onPreview?.(i)}
            data-testid={`avatar-image-${avatar.id}-${i}`}
          >
            <img
              src={img}
              alt={`${avatar.name} ${i + 1}`}
              className="w-full h-full object-cover"
              loading="lazy"
            />
            <div className="absolute inset-0 bg-black/0 group-hover/img:bg-black/20 transition-colors flex items-center justify-center">
              <ZoomIn className="w-4 h-4 text-white opacity-0 group-hover/img:opacity-100 transition-opacity" />
            </div>
          </button>
        ))}
      </div>
      {/* Info */}
      <div className="p-3">
        <div className="flex items-center justify-between gap-1">
          <h3 className="text-sm font-semibold truncate">{avatar.name}</h3>
          {avatar.is_preset && (
            <span className="shrink-0 text-[9px] bg-primary/10 text-primary px-1.5 py-0.5 rounded-full font-medium">Preset</span>
          )}
        </div>
        {avatar.description && (
          <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{avatar.description}</p>
        )}
        {/* Add to My Avatars button for presets */}
        {onCopy && avatar.is_preset && (
          <button
            onClick={(e) => { e.stopPropagation(); onCopy(avatar.id); }}
            className="mt-2 w-full flex items-center justify-center gap-1 text-[11px] font-medium text-primary hover:bg-primary/5 rounded-md py-1.5 transition-colors border border-primary/20"
            data-testid={`copy-avatar-${avatar.id}`}
          >
            <Copy className="w-3 h-3" />Add to My Avatars
          </button>
        )}
      </div>
      {/* Action buttons */}
      <div className="absolute top-1.5 right-1.5 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
        {onEdit && (
          <button
            onClick={(e) => { e.stopPropagation(); onEdit(avatar); }}
            className="w-6 h-6 bg-black/60 text-white rounded-full flex items-center justify-center hover:bg-black/80"
            data-testid={`edit-avatar-${avatar.id}`}
          >
            <Pencil className="w-3 h-3" />
          </button>
        )}
        {onDelete && (
          <button
            onClick={(e) => { e.stopPropagation(); onDelete(avatar.id); }}
            className="w-6 h-6 bg-black/60 text-white rounded-full flex items-center justify-center hover:bg-red-600/80"
            data-testid={`delete-avatar-${avatar.id}`}
          >
            <Trash2 className="w-3 h-3" />
          </button>
        )}
      </div>
    </div>
  );
}


function EditAvatarForm({ avatar, onSuccess, onCancel }) {
  const [name, setName] = useState(avatar.name || "");
  const [description, setDescription] = useState(avatar.description || "");
  const [files, setFiles] = useState([null, null, null]);
  const [previews, setPreviews] = useState(avatar.images || [null, null, null]);
  const [submitting, setSubmitting] = useState(false);
  const fileRefs = [useRef(), useRef(), useRef()];

  const handleFileChange = (index, file) => {
    if (!file) return;
    const newFiles = [...files];
    newFiles[index] = file;
    setFiles(newFiles);

    const reader = new FileReader();
    reader.onload = (e) => {
      const newPreviews = [...previews];
      newPreviews[index] = e.target.result;
      setPreviews(newPreviews);
    };
    reader.readAsDataURL(file);
  };

  const handleSubmit = async () => {
    if (!name.trim()) return toast.error("Name is required");

    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append("name", name.trim());
      formData.append("description", description.trim());
      if (files[0]) formData.append("image1", files[0]);
      if (files[1]) formData.append("image2", files[1]);
      if (files[2]) formData.append("image3", files[2]);

      await axios.put(`${API}/api/avatars/${avatar.id}`, formData, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Avatar updated!");
      onSuccess();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update avatar");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="rounded-xl border border-border bg-card p-5 space-y-4" data-testid="edit-avatar-form">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-semibold text-sm">Edit Avatar</h3>
          <p className="text-xs text-muted-foreground">Update name, description, or replace images</p>
        </div>
        <button onClick={onCancel} className="text-muted-foreground hover:text-foreground">
          <X className="w-4 h-4" />
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label className="text-xs">Name</Label>
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            data-testid="edit-avatar-name"
          />
        </div>
        <div className="space-y-2">
          <Label className="text-xs">Description</Label>
          <Input
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            data-testid="edit-avatar-description"
          />
        </div>
      </div>

      <div>
        <Label className="text-xs mb-2 block">Images (click to replace)</Label>
        <div className="grid grid-cols-3 gap-3">
          {[0, 1, 2].map((i) => (
            <div key={i} className="relative">
              <input
                ref={fileRefs[i]}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => handleFileChange(i, e.target.files[0])}
                data-testid={`edit-avatar-image-${i + 1}`}
              />
              <button
                type="button"
                onClick={() => fileRefs[i].current?.click()}
                className="w-full aspect-square rounded-lg border-2 border-dashed border-border hover:border-primary/50 transition-colors flex flex-col items-center justify-center overflow-hidden bg-muted/30 relative group/edit"
              >
                {previews[i] ? (
                  <>
                    <img src={previews[i]} alt={`Image ${i + 1}`} className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-black/0 group-hover/edit:bg-black/30 transition-colors flex items-center justify-center">
                      <Pencil className="w-4 h-4 text-white opacity-0 group-hover/edit:opacity-100 transition-opacity" />
                    </div>
                  </>
                ) : (
                  <>
                    <Upload className="w-5 h-5 text-muted-foreground mb-1" />
                    <span className="text-[10px] text-muted-foreground">Image {i + 1}</span>
                  </>
                )}
              </button>
              {files[i] && (
                <span className="absolute -top-1 -right-1 w-4 h-4 bg-primary rounded-full flex items-center justify-center">
                  <span className="text-[8px] text-white font-bold">!</span>
                </span>
              )}
            </div>
          ))}
        </div>
        <p className="text-[10px] text-muted-foreground mt-1">Only replaced images will be re-uploaded</p>
      </div>

      <div className="flex justify-end gap-2">
        <Button variant="outline" size="sm" onClick={onCancel} className="rounded-full">Cancel</Button>
        <Button
          size="sm"
          onClick={handleSubmit}
          disabled={submitting}
          className="rounded-full gap-1"
          data-testid="edit-avatar-submit"
        >
          {submitting ? <Loader2 className="w-3 h-3 animate-spin" /> : <Pencil className="w-3 h-3" />}
          {submitting ? "Saving..." : "Save Changes"}
        </Button>
      </div>
    </div>
  );
}


function AvatarLightbox({ avatar, initialIndex, onClose }) {
  const [index, setIndex] = useState(initialIndex || 0);
  const images = avatar.images || [];

  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") setIndex((p) => (p + 1) % images.length);
      if (e.key === "ArrowLeft") setIndex((p) => (p - 1 + images.length) % images.length);
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [images.length, onClose]);

  if (images.length === 0) return null;

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-center justify-center"
      onClick={onClose}
      data-testid="avatar-lightbox"
    >
      <div
        className="relative max-w-lg w-full mx-4"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute -top-10 right-0 text-white/70 hover:text-white transition-colors"
          data-testid="lightbox-close"
        >
          <X className="w-6 h-6" />
        </button>

        {/* Avatar name */}
        <div className="text-center mb-4">
          <h3 className="text-white font-semibold text-lg">{avatar.name}</h3>
          {avatar.description && <p className="text-white/60 text-sm">{avatar.description}</p>}
        </div>

        {/* Main image */}
        <div className="relative rounded-xl overflow-hidden bg-black aspect-[3/4]">
          <img
            src={images[index]}
            alt={`${avatar.name} ${index + 1}`}
            className="w-full h-full object-contain"
            data-testid="lightbox-main-image"
          />

          {/* Nav arrows */}
          {images.length > 1 && (
            <>
              <button
                onClick={() => setIndex((p) => (p - 1 + images.length) % images.length)}
                className="absolute left-2 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-black/50 text-white flex items-center justify-center hover:bg-black/70 transition-colors"
                data-testid="lightbox-prev"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <button
                onClick={() => setIndex((p) => (p + 1) % images.length)}
                className="absolute right-2 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-black/50 text-white flex items-center justify-center hover:bg-black/70 transition-colors"
                data-testid="lightbox-next"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </>
          )}
        </div>

        {/* Thumbnails */}
        <div className="flex justify-center gap-2 mt-4">
          {images.map((img, i) => (
            <button
              key={i}
              onClick={() => setIndex(i)}
              className={`w-16 h-16 rounded-lg overflow-hidden border-2 transition-all ${
                i === index ? "border-white scale-105" : "border-transparent opacity-60 hover:opacity-90"
              }`}
              data-testid={`lightbox-thumb-${i}`}
            >
              <img src={img} alt={`Thumb ${i + 1}`} className="w-full h-full object-cover" />
            </button>
          ))}
        </div>

        {/* Counter */}
        <p className="text-center text-white/50 text-xs mt-2">{index + 1} / {images.length}</p>
      </div>
    </div>
  );
}


function AvatarForm({ title, subtitle, endpoint, onSuccess, onCancel, testIdPrefix }) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [files, setFiles] = useState([null, null, null]);
  const [previews, setPreviews] = useState([null, null, null]);
  const [submitting, setSubmitting] = useState(false);
  const fileRefs = [useRef(), useRef(), useRef()];

  const handleFileChange = (index, file) => {
    if (!file) return;
    const newFiles = [...files];
    newFiles[index] = file;
    setFiles(newFiles);

    const reader = new FileReader();
    reader.onload = (e) => {
      const newPreviews = [...previews];
      newPreviews[index] = e.target.result;
      setPreviews(newPreviews);
    };
    reader.readAsDataURL(file);
  };

  const handleSubmit = async () => {
    if (!name.trim()) return toast.error("Name is required");
    if (files.some((f) => !f)) return toast.error("All 3 images are required");

    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append("name", name.trim());
      formData.append("description", description.trim());
      formData.append("image1", files[0]);
      formData.append("image2", files[1]);
      formData.append("image3", files[2]);

      await axios.post(endpoint, formData, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Avatar created!");
      onSuccess();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create avatar");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="rounded-xl border border-border bg-card p-5 space-y-4" data-testid={`${testIdPrefix}-avatar-form`}>
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-semibold text-sm">{title}</h3>
          <p className="text-xs text-muted-foreground">{subtitle}</p>
        </div>
        <button onClick={onCancel} className="text-muted-foreground hover:text-foreground">
          <X className="w-4 h-4" />
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label className="text-xs">Name</Label>
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Sarah, Professional"
            data-testid={`${testIdPrefix}-avatar-name`}
          />
        </div>
        <div className="space-y-2">
          <Label className="text-xs">Description</Label>
          <Input
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Short description..."
            data-testid={`${testIdPrefix}-avatar-description`}
          />
        </div>
      </div>

      {/* Image uploads */}
      <div>
        <Label className="text-xs mb-2 block">Reference Images (3 required)</Label>
        <div className="grid grid-cols-3 gap-3">
          {[0, 1, 2].map((i) => (
            <div key={i} className="relative">
              <input
                ref={fileRefs[i]}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => handleFileChange(i, e.target.files[0])}
                data-testid={`${testIdPrefix}-avatar-image-${i + 1}`}
              />
              <button
                type="button"
                onClick={() => fileRefs[i].current?.click()}
                className="w-full aspect-square rounded-lg border-2 border-dashed border-border hover:border-primary/50 transition-colors flex flex-col items-center justify-center overflow-hidden bg-muted/30"
              >
                {previews[i] ? (
                  <img src={previews[i]} alt={`Preview ${i + 1}`} className="w-full h-full object-cover" />
                ) : (
                  <>
                    <Upload className="w-5 h-5 text-muted-foreground mb-1" />
                    <span className="text-[10px] text-muted-foreground">Image {i + 1}</span>
                  </>
                )}
              </button>
            </div>
          ))}
        </div>
      </div>

      <div className="flex justify-end gap-2">
        <Button variant="outline" size="sm" onClick={onCancel} className="rounded-full">Cancel</Button>
        <Button
          size="sm"
          onClick={handleSubmit}
          disabled={submitting}
          className="rounded-full gap-1"
          data-testid={`${testIdPrefix}-avatar-submit`}
        >
          {submitting ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3" />}
          {submitting ? "Creating..." : "Create Avatar"}
        </Button>
      </div>
    </div>
  );
}
